import { ReplaySubject, Observable, of, combineLatest } from 'rxjs';
import { startWith, switchMap, shareReplay, map, filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import {
  IFsLprProviderInterface,
  LPRResultsCameraVendor,
  LPRResultsCameraModel,
} from '../../../../../api/interfaces';
import { ViLPRProvider } from '../../../../../shared/models';
import { WWA_VENDOR_SEPARATOR } from '../../../../../utils';

/**
 * Provides reactive streams for source selection dropdown
 * Tracks each provider's load state, result counts, and available camera models
 * Automatically updates UI when providers finish loading or new data arrives
 */
@Injectable()
export class LPRSourceSelectService {
  private static readonly AXON_VENDOR_NAME = 'axon';

  providers$: ReplaySubject<IFsLprProviderInterface[]>;

  areAllProvidersLoaded$: Observable<boolean>;

  axonVIProvider$: Observable<IFsLprProviderInterface | undefined>;
  isAxonVIProviderLoaded$: Observable<boolean>;
  axonCameraVendor$: Observable<LPRResultsCameraVendor | undefined>;
  axonCameraModels$: Observable<LPRResultsCameraModel[]>;

  private worksWithAxonCameraVendors$: Observable<LPRResultsCameraVendor[]>;
  worksWithAxonCount$: Observable<number>;
  worksWithAxonCameraModels$: Observable<LPRResultsCameraModel[]>;

  vigilantProvider$: Observable<IFsLprProviderInterface | undefined>;
  isVigilantProviderLoaded$: Observable<boolean>;

  flockProvider$: Observable<IFsLprProviderInterface | undefined>;
  isFlockProviderLoaded$: Observable<boolean>;

  flockV3Provider$: Observable<IFsLprProviderInterface | undefined>;
  isFlockV3ProviderLoaded$: Observable<boolean>;

  fususProvider$: Observable<IFsLprProviderInterface | undefined>;
  isFususProviderLoaded$: Observable<boolean>;

  /** Sets up all reactive streams for tracking provider states and camera model data */
  initialize(providers$: ReplaySubject<IFsLprProviderInterface[]>): void {
    this.providers$ = providers$;

    this.setAreAllProvidersLoadedSubscription();

    this.setProviderSubscriptions();
    this.setIsProviderLoadedSubscriptions();
    this.setCameraVendorSubscriptions();

    this.setAxonCameraModelsSubscription();

    /**
     * WWA needs to be treated differently since it's not a real provider
     * It's derived from Axon VI's camera vendors (all except Axon 1Ps)
     */
    this.setWorksWithAxonCameraVendorsSubscription();
    this.setWorksWithAxonCountSubscription();
    this.setWorksWithAxonCameraModelsSubscription();
  }

  private setAreAllProvidersLoadedSubscription(): void {
    this.areAllProvidersLoaded$ = this.providers$.pipe(
      switchMap((providers) => {
        if (providers.length === 0) {
          return of([]);
        }

        const allProvidersLoaded$ = providers.map((provider) =>
          provider.loaded$.pipe(startWith(!provider.isLoading)),
        );

        return combineLatest(allProvidersLoaded$);
      }),
      map((providersLoadingStates) => providersLoadingStates.every((state) => state === true)),
      shareReplay(1),
    );
  }

  private setProviderSubscriptions(): void {
    this.axonVIProvider$ = this.getProviderSubscription(ViLPRProvider.AXON_VI);
    this.vigilantProvider$ = this.getProviderSubscription(ViLPRProvider.VIGILANT);
    this.flockProvider$ = this.getProviderSubscription(ViLPRProvider.FLOCK);
    this.flockV3Provider$ = this.getProviderSubscription(ViLPRProvider.FLOCK_V3);
    this.fususProvider$ = this.getProviderSubscription(ViLPRProvider.FUSUS);
  }

  private setIsProviderLoadedSubscriptions(): void {
    this.isAxonVIProviderLoaded$ = this.getIsProviderLoadedSubscription(this.axonVIProvider$);
    this.isVigilantProviderLoaded$ = this.getIsProviderLoadedSubscription(this.vigilantProvider$);
    this.isFlockProviderLoaded$ = this.getIsProviderLoadedSubscription(this.flockProvider$);
    this.isFlockV3ProviderLoaded$ = this.getIsProviderLoadedSubscription(this.flockV3Provider$);
    this.isFususProviderLoaded$ = this.getIsProviderLoadedSubscription(this.fususProvider$);
  }

  private setCameraVendorSubscriptions(): void {
    this.axonCameraVendor$ = this.getAxonCameraVendorSubscription(
      this.isAxonVIProviderLoaded$,
      this.axonVIProvider$,
    );
  }

  private setAxonCameraModelsSubscription(): void {
    this.axonCameraModels$ = this.axonCameraVendor$.pipe(
      map((vendor) => vendor?.cameraModels || []),
      shareReplay(1),
    );
  }

  private setWorksWithAxonCameraVendorsSubscription(): void {
    this.worksWithAxonCameraVendors$ = this.axonVIProvider$.pipe(
      switchMap((provider) => {
        if (!provider) {
          return of([]);
        }
        return this.isAxonVIProviderLoaded$.pipe(
          filter((isLoaded) => isLoaded),
          map(() => {
            const normalizedVendors = this.ensureAxonFirst(provider.facets?.cameraVendors);

            return normalizedVendors?.slice(1) || [];
          }),
        );
      }),
      shareReplay(1),
    );
  }

  private setWorksWithAxonCountSubscription(): void {
    this.worksWithAxonCount$ = this.worksWithAxonCameraVendors$.pipe(
      map((vendors) => vendors.reduce((total, vendor) => total + (vendor.count || 0), 0)),
      shareReplay(1),
    );
  }

  private setWorksWithAxonCameraModelsSubscription(): void {
    this.worksWithAxonCameraModels$ = this.worksWithAxonCameraVendors$.pipe(
      map((vendors) =>
        vendors.flatMap((vendor) =>
          (vendor.cameraModels || []).map((model) => ({
            ...model,
            model:
              // WWA camera models are formatted as "vendor.model", e.g. "jenoptik.vector", "insightalpr.model1"
              `${vendor.vendor}${WWA_VENDOR_SEPARATOR}${model.model}`.trim(),
          })),
        ),
      ),
      shareReplay(1),
    );
  }

  private getProviderSubscription(
    name: ViLPRProvider,
  ): Observable<IFsLprProviderInterface | undefined> {
    return this.providers$.pipe(
      map((providers) => providers.find((provider) => provider.nameEnum === name)),
      shareReplay(1),
    );
  }

  private getIsProviderLoadedSubscription(
    provider$: Observable<IFsLprProviderInterface | undefined>,
  ): Observable<boolean> {
    return provider$.pipe(
      switchMap((provider) => {
        if (!provider) {
          return of(false);
        }
        return provider.loaded$.pipe(startWith(!provider.isLoading));
      }),
      shareReplay(1),
    );
  }

  /**
   * Extracts camera vendor data from Axon VI provider
   * Note: cameraVendors is only present on Axon providers
   */
  private getAxonCameraVendorSubscription(
    isProviderLoaded$: Observable<boolean>,
    provider$: Observable<IFsLprProviderInterface | undefined>,
  ): Observable<LPRResultsCameraVendor | undefined> {
    return provider$.pipe(
      switchMap((provider) => {
        if (!provider) {
          return of(undefined);
        }
        return isProviderLoaded$.pipe(
          filter((isLoaded) => isLoaded),
          map(() => {
            const normalizedVendors = this.ensureAxonFirst(provider.facets?.cameraVendors);

            return normalizedVendors?.[0];
          }),
        );
      }),
      shareReplay(1),
    );
  }

  /**
   * Ensures Axon vendor is always first in the list (UI convention)
   * Creates an empty Axon vendor if missing (e.g., when only WWA results exist)
   */
  private ensureAxonFirst(
    cameraVendors: LPRResultsCameraVendor[] | undefined,
  ): LPRResultsCameraVendor[] | undefined {
    if (!cameraVendors || cameraVendors.length === 0) {
      return cameraVendors;
    }

    const firstVendor = cameraVendors[0];

    if (firstVendor?.vendor === LPRSourceSelectService.AXON_VENDOR_NAME) {
      return cameraVendors;
    }

    const dummyAxonVendor: LPRResultsCameraVendor = {
      vendor: LPRSourceSelectService.AXON_VENDOR_NAME,
      count: 0,
      cameraModels: [],
    };

    return [dummyAxonVendor, ...cameraVendors];
  }
}
